import React, {useCallback, useMemo, useState} from 'react';
import {
    Modal,
    Button,
    DropdownButton,
    Dropdown,
    Spinner,
} from 'react-bootstrap';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {Icon} from 'shared/helpers';
import {produce} from 'immer';

import styled from 'styled-components';

type ConfirmationButton = {
    title?: string;
    name?: string;
    variant?: string;
    action?: () => void | Promise<void>;
    show?: boolean;
    controlledHideDialog?: boolean;
    disabled?: boolean;
    alignLeft?: boolean;
    iconName?: string;
    isLoading?: boolean;
    options?: {
        name?: string;
        action?: () => void | Promise<void>;
    }[];
    className?: string;
    style?: React.CSSProperties;
    autoFocus?: boolean;
};

export type ConfirmationOption = {
    title?: string;
    message?: string | JSX.Element;
    container?: string;
    className?: string;
    hideFooter?: boolean;
    hideYesButton?: boolean;
    yes?: () => void | Promise<void>;
    hideNoButton?: boolean;
    no?: () => void | Promise<void>;
    buttons?: ConfirmationButton[];
    keyboard?: boolean;
    setShow?: boolean;
    modalDialogClass?: string;
    size?: 'sm' | 'lg' | 'xl';
    maxWidth?: number;
    headerClass?: string;
    headerStyle?: React.CSSProperties;
    bodyClass?: string;
    footerClass?: string;
    closeButton?: boolean;
    onHide?: () => void;
};

const Confirmation = {
    YES: true,
    NO: false,
};

export const useConfirmationDialog = (
    title_?: string,
    message_?: string,
    backdrop: 'static' | boolean = 'static'
) => {
    const [confirmed, setConfirmed] = useState<boolean>(Confirmation.NO);
    const [show, setShow] = useState(false);
    const [options, setOptions] = useState<ConfirmationOption>({});
    const [title, setTitle] = useState<string>(title_ ? title_ : '');
    const [message, setMessage] = useState<string | JSX.Element>(
        message_ ? message_ : ''
    );

    const updateOptions = useCallback((option: Partial<ConfirmationOption>) => {
        setOptions((options) =>
            produce(options, (draft) => {
                const {buttons, ...otherOptions} = option;

                // Merge non-array properties
                Object.assign(draft, otherOptions);

                // Merge buttons
                if (buttons && draft.buttons) {
                    buttons.forEach((btn, index) => {
                        if (draft.buttons[Number(index)]) {
                            Object.assign(draft.buttons[Number(index)], btn);
                        } else {
                            draft.buttons[Number(index)] = btn;
                        }
                    });
                } else if (buttons) {
                    draft.buttons = buttons;
                }
            })
        );
    }, []);

    const showDialog = (options: ConfirmationOption) => {
        if (typeof options !== 'undefined') {
            setOptions(options);

            if (options.title) setTitle(options.title);
            if (options.message) setMessage(options.message);
        }

        setShow(true);
    };

    const hideDialog = useCallback(() => {
        setShow(false);
    }, []);

    const dialog = useMemo(
        () => (
            <ConfirmationDialog
                title={title}
                message={message}
                show={show}
                setShow={setShow}
                setConfirmed={setConfirmed}
                hideDialog={hideDialog}
                options={options}
                backdrop={backdrop}
            />
        ),
        [title, message, show, options, backdrop]
    );

    return {
        show,
        confirmed,
        setConfirmed,
        dialog,
        showDialog,
        hideDialog,
        setTitle,
        setMessage,
        updateOptions,
    };
};

interface ConfirmationDialogProps {
    title?: string;
    message?: string | JSX.Element;
    show: boolean;
    setShow?: (show: boolean) => void;
    setConfirmed?: (show: boolean) => void;
    hideDialog: () => void;
    options: ConfirmationOption;
    backdrop: boolean | 'static';
}

export const ConfirmationDialog = ({
    title,
    message,
    show,
    setShow,
    setConfirmed,
    hideDialog,
    options,
    backdrop,
}: ConfirmationDialogProps) => {
    const onHideHandler = useCallback(() => {
        setShow && setShow(false);

        if (options && typeof options.onHide == 'function') {
            options.onHide();
        }
    }, [setShow, options]);

    const yesButtonHandler = useCallback(() => {
        setConfirmed && setConfirmed(Confirmation.YES);

        hideDialog();

        if (options.yes) {
            void options.yes();
        }
    }, [setConfirmed, options, hideDialog]);

    const noButtonHandler = useCallback(() => {
        setConfirmed && setConfirmed(Confirmation.NO);

        if (options.no) {
            void options.no();
        }

        hideDialog();
    }, [setConfirmed, options, hideDialog]);

    const customButtonHandler = useCallback(
        (controlledButton: boolean, action: () => void | Promise<void>) =>
            () => {
                if (!controlledButton) hideDialog();

                if (action) {
                    void action();
                }
            },
        [hideDialog]
    );

    return (
        <ConfirmationDialogElement
            dialogClassName={
                options.hasOwnProperty('modalDialogClass')
                    ? options.modalDialogClass
                    : ''
            }
            show={show}
            onHide={onHideHandler}
            backdropClassName={
                options.hasOwnProperty('container') ? 'absolute-backdrop' : ''
            }
            container={
                options.hasOwnProperty('container')
                    ? document.querySelector<HTMLElement>(options.container)
                    : undefined
            }
            backdrop={backdrop}
            className={
                options.className
                    ? `confirmationDialog ${options.className}`
                    : 'confirmationDialog'
            }
            keyboard={
                options.hasOwnProperty('keyboard') ? options.keyboard : true
            }
            size={options.hasOwnProperty('size') ? options.size : undefined}
            centered={true}
            restoreFocus={false}
            animation={false}
            $maxWidth={options.maxWidth}>
            <Modal.Header
                className={options?.headerClass}
                closeButton={options.closeButton}
                style={options.headerStyle || {}}>
                <Modal.Title>
                    <strong>{parseHtmlString(title)}</strong>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={options?.bodyClass}>
                {typeof message === 'object'
                    ? message
                    : parseHtmlString(message)}
            </Modal.Body>
            {options.hasOwnProperty('hideFooter') &&
            options.hideFooter ? null : (
                <Modal.Footer className={options?.footerClass}>
                    {options.hasOwnProperty('hideYesButton') &&
                    options.hideYesButton ? null : (
                        <Button variant="secondary" onClick={yesButtonHandler}>
                            Yes
                        </Button>
                    )}

                    {options.hasOwnProperty('hideNoButton') &&
                    options.hideNoButton ? null : (
                        <Button variant="primary" onClick={noButtonHandler}>
                            No
                        </Button>
                    )}

                    {options.buttons
                        ? options.buttons.map((button: ConfirmationButton) => {
                              if (button.show) {
                                  if (button.name != 'moreOptions') {
                                      const buttonControlledHideDialog =
                                          button.hasOwnProperty(
                                              'controlledHideDialog'
                                          ) && button.controlledHideDialog;
                                      const disabled =
                                          button.hasOwnProperty('disabled') &&
                                          button.disabled
                                              ? true
                                              : false;
                                      const isLoading = !!button?.isLoading;
                                      return (
                                          <Button
                                              title={
                                                  button.title
                                                      ? button.title
                                                      : button.name
                                              }
                                              key={button.name}
                                              variant={
                                                  button.hasOwnProperty(
                                                      'variant'
                                                  )
                                                      ? button.variant
                                                      : 'primary'
                                              }
                                              onClick={customButtonHandler(
                                                  buttonControlledHideDialog,
                                                  button.action
                                              )}
                                              autoFocus={button.autoFocus}
                                              className={button.className}
                                              style={{
                                                  ...(button.style || {}),
                                                  ...(button.hasOwnProperty(
                                                      'alignLeft'
                                                  ) && button.alignLeft
                                                      ? {marginRight: 'auto'}
                                                      : {}),
                                                  ...(button.hasOwnProperty(
                                                      'iconName'
                                                  )
                                                      ? {
                                                            padding:
                                                                '0 10px 0 10px',
                                                        }
                                                      : {}),
                                              }}
                                              disabled={disabled || isLoading}>
                                              {button.hasOwnProperty(
                                                  'iconName'
                                              ) ? (
                                                  <JoinIcon
                                                      iconName={button.iconName}
                                                      color="#ffffff"
                                                  />
                                              ) : null}
                                              {button.hasOwnProperty('name')
                                                  ? button.name
                                                  : 'Button'}
                                              {isLoading ? (
                                                  <Spinner
                                                      style={{
                                                          width: '14px',
                                                          height: '14px',
                                                          marginLeft: '4px',
                                                      }}
                                                      animation="border"
                                                      role="status">
                                                      <span className="visually-hidden">
                                                          Loading...
                                                      </span>
                                                  </Spinner>
                                              ) : null}
                                          </Button>
                                      );
                                  } else {
                                      return (
                                          <DropdownButton
                                              key={button.name}
                                              align="end"
                                              title="More Options"
                                              className="jobStatusDropdown"
                                              id="dropdown-menu-align-right">
                                              {button.options
                                                  ? button.options.map(
                                                        (option) => {
                                                            return (
                                                                <StyledDropDownItem
                                                                    key={
                                                                        option.name
                                                                    }
                                                                    onClick={customButtonHandler(
                                                                        false,
                                                                        option.action
                                                                    )}>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </StyledDropDownItem>
                                                            );
                                                        }
                                                    )
                                                  : null}
                                          </DropdownButton>
                                      );
                                  }
                              }
                          })
                        : null}
                </Modal.Footer>
            )}
        </ConfirmationDialogElement>
    );
};

const StyledDropDownItem = styled(Dropdown.Item)`
    padding: 8px 13px !important;
    &:hover {
        background-color: rgba(var(--menu_primary_colour), 0.85) !important;
    }
`;

const JoinIcon = styled(Icon)`
    width: 30px;
    height: 30px;
    margin-right: 5px;
`;

const ConfirmationDialogElement = styled(Modal)<{$maxWidth?: number}>`
    .btn-primary {
        background: ${({theme}) => theme?.colors?.secondary?.main};
        border-color: ${({theme}) => theme?.colors?.secondary?.main};
    }
    .btn-primary:hover,
    .btn-primary:focus,
    .btn-primary:active {
        background: ${({theme}) => theme?.colors?.primary?.main} !important;
        border-color: ${({theme}) => theme?.colors?.primary?.main} !important;
    }

    .btn-primary-color {
        background: ${({theme}) => theme?.colors?.primary?.main};
        border-color: ${({theme}) => theme?.colors?.primary?.main};
        color: white;
    }
    .btn-primary-color:hover,
    .btn-primary-color:focus,
    .btn-primary-color:active {
        background: ${({theme}) => theme?.colors?.secondary?.main};
        border-color: ${({theme}) => theme?.colors?.secondary?.main};
        color: white;
    }

    .btn-secondary,
    .btn-secondary:hover,
    .btn-secondary:focus,
    .btn-secondary:active {
        background: #c92434;
        border-color: #c92434;
    }

    > .modal-dialog {
        ${({$maxWidth}) => ($maxWidth ? `max-width: ${$maxWidth}px;` : '')}
    }
    &.share-qr {
        // tawk chat is overlaying on mobile -- this forces QR modal on top
        z-index: calc(infinity);
    }
`;
